import type { CreateJobInput } from '@kiku/jobs/types';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { QUESTION_CATEGORIES } from '~/components/features/interviews/interviews.types';
import type { WizardQuestion } from '~/components/features/interviews/interviews.types';
import { BasicInfoStep } from '~/components/features/interviews/wizard-steps/basic-info-step';
import { ConfirmStep } from '~/components/features/interviews/wizard-steps/confirm-step';
import { QuestionsStep } from '~/components/features/interviews/wizard-steps/questions-step';
import { Button } from '~/components/ui/button';
import { useDocumentTitle } from '~/hooks/use-document-title';
import { useSlugIds } from '~/hooks/use-slug-ids';
import { api } from '~/lib/api';
import { i18n } from '~/lib/i18n';

export const Route = createFileRoute('/_authenticated/org/$orgSlug/department/$deptSlug/jobs/new/')(
  {
    component: NewJobWizard
  }
);

type WizardState = {
  step: 1 | 2 | 3;
  jobId: string | null;
  basicInfo: CreateJobInput | null;
  questions: WizardQuestion[];
};

type WizardContextType = WizardState & {
  setState: React.Dispatch<React.SetStateAction<WizardState>>;
  orgId: string;
  deptId: string;
  orgSlug: string;
  deptSlug: string;
};

const WizardContext = createContext<WizardContextType | null>(null);

function useWizard(): WizardContextType {
  const ctx = useContext(WizardContext);
  if (!ctx) throw new Error('useWizard must be used inside WizardContext');
  return ctx;
}

function WizardHeader({ step }: { step: number }): React.ReactNode {
  const { t } = useTranslation();
  const steps = [
    t('wizard.manual.step1Title'),
    t('wizard.manual.step2Title'),
    t('wizard.manual.step3Title')
  ];

  return (
    <div className="flex items-center gap-4 pb-6">
      <h1 className="text-2xl">{t('wizard.manual.title')}</h1>
      <div className="flex items-center gap-2">
        {steps.map((label, i) => (
          <div key={label} className="flex items-center gap-2">
            <div
              className={`flex size-6 items-center justify-center rounded-full text-xs font-medium ${
                i + 1 === step
                  ? 'bg-primary text-primary-foreground'
                  : i + 1 < step
                    ? 'bg-primary/20 text-primary'
                    : 'bg-secondary text-muted-foreground'
              }`}
            >
              {i + 1}
            </div>
            <span
              className={`hidden text-sm sm:inline ${i + 1 === step ? 'font-medium text-foreground' : 'text-muted-foreground'}`}
            >
              {label}
            </span>
            {i < steps.length - 1 && <div className="h-px w-6 bg-border" />}
          </div>
        ))}
      </div>
    </div>
  );
}

function Step1(): React.ReactNode {
  const { t } = useTranslation();
  const { setState, orgId, deptId } = useWizard();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(data: CreateJobInput): Promise<void> {
    setIsSubmitting(true);
    setError(null);
    try {
      const res = await api.api.org[':orgId'].department[':deptId'].jobs.$post({
        param: { orgId, deptId },
        json: data
      });
      const { job } = await res.json();
      setState((prev) => ({
        ...prev,
        step: 2,
        jobId: job.id,
        basicInfo: data
      }));
    } catch {
      setError(t('jobs.createError'));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      {error && (
        <div className="mb-4 rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}
      <BasicInfoStep onSubmit={(d) => void handleSubmit(d)} isSubmitting={isSubmitting} />
    </>
  );
}

function Step2(): React.ReactNode {
  const { t } = useTranslation();
  const { setState, jobId, questions, orgId, deptId } = useWizard();
  const [isGenerating, setIsGenerating] = useState(true);
  const [generateError, setGenerateError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const generateQuestions = useCallback(async (): Promise<void> => {
    if (!jobId) return;
    setIsGenerating(true);
    setGenerateError(false);
    try {
      const res = await api.api.org[':orgId'].department[':deptId'].jobs[':jobId'][
        'interview-setup'
      ].questions.generate.$post({ param: { orgId, deptId, jobId } });
      if (!res.ok) throw new Error(i18n.t('errors.unexpected'));
      const { questions: generated } = await res.json();
      setState((prev) => ({
        ...prev,
        questions: generated.map((q) => ({
          id: q.id,
          category: q.category,
          text: q.text,
          position: q.position,
          isOptional: q.isOptional,
          competencyName: q.competencyName ?? undefined,
          scoringExamples: q.scoringExamples ?? undefined,
          aiGenerated: q.aiGenerated
        }))
      }));
    } catch {
      setGenerateError(true);
    } finally {
      setIsGenerating(false);
    }
  }, [jobId, orgId, deptId, setState]);

  // Auto-generate on mount — skip if questions already loaded (e.g. navigating back then forward)
  useEffect(() => {
    if (questions.length > 0) {
      setIsGenerating(false);
      return;
    }
    void generateQuestions();
  }, [generateQuestions]);

  async function handleSave(): Promise<void> {
    if (!jobId) return;
    setIsSaving(true);
    try {
      const normalizedQuestions = QUESTION_CATEGORIES.flatMap((category) =>
        questions.filter((q) => q.category === category).sort((a, b) => a.position - b.position)
      ).map((q, position) => ({
        category: q.category,
        text: q.text,
        position,
        isOptional: q.isOptional,
        competencyName: q.competencyName,
        scoringExamples: q.scoringExamples
      }));
      await api.api.org[':orgId'].department[':deptId'].jobs[':jobId'][
        'interview-setup'
      ].questions.$put({
        param: { orgId, deptId, jobId },
        json: { questions: normalizedQuestions }
      });
      await api.api.org[':orgId'].department[':deptId'].jobs[':jobId']['setup-status'].$patch({
        param: { orgId, deptId, jobId },
        json: { setupStatus: 'ready' }
      });
      setState((prev) => ({ ...prev, step: 3 }));
    } finally {
      setIsSaving(false);
    }
  }

  if (generateError) {
    return (
      <div className="flex flex-col items-center gap-4 py-12">
        <p className="text-sm text-destructive">{t('wizard.questions.generateError')}</p>
        <Button variant="outline" onClick={() => void generateQuestions()}>
          {t('wizard.questions.retry')}
        </Button>
      </div>
    );
  }

  return (
    <QuestionsStep
      questions={questions}
      isGenerating={isGenerating}
      onQuestionsChange={(qs) => {
        setState((prev) => ({ ...prev, questions: qs }));
      }}
      onBack={() => {
        setState((prev) => ({ ...prev, step: 1 }));
      }}
      onSave={() => void handleSave()}
      isSaving={isSaving}
    />
  );
}

function Step3(): React.ReactNode {
  const { jobId, basicInfo, questions, orgId, deptId, orgSlug, deptSlug, setState } = useWizard();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleGoLive(): Promise<void> {
    if (!jobId) return;
    setIsSubmitting(true);
    try {
      await api.api.org[':orgId'].department[':deptId'].jobs[':jobId']['setup-status'].$patch({
        param: { orgId, deptId, jobId },
        json: { setupStatus: 'active' }
      });
      void navigate({
        to: '/org/$orgSlug/department/$deptSlug/jobs',
        params: { orgSlug, deptSlug }
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <ConfirmStep
      jobs={basicInfo ? [{ title: basicInfo.title, description: basicInfo.description }] : []}
      questions={questions}
      modality={basicInfo?.modality ?? ['text']}
      languages={basicInfo?.languages ?? []}
      onBack={() => {
        setState((prev) => ({ ...prev, step: 2 }));
      }}
      onGoLive={() => void handleGoLive()}
      isSubmitting={isSubmitting}
    />
  );
}

function NewJobWizard(): React.ReactNode {
  const { t } = useTranslation();
  useDocumentTitle(t('wizard.manual.title'));
  const { orgSlug, deptSlug } = Route.useParams();
  const { orgId, deptId } = useSlugIds(orgSlug, deptSlug);

  const [state, setState] = useState<WizardState>({
    step: 1,
    jobId: null,
    basicInfo: null,
    questions: []
  });

  return (
    <WizardContext.Provider
      value={{ ...state, setState, orgId, deptId: deptId ?? '', orgSlug, deptSlug }}
    >
      <div className="flex flex-col gap-0 p-8">
        <WizardHeader step={state.step} />
        <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
          {state.step === 1 && <Step1 />}
          {state.step === 2 && <Step2 />}
          {state.step === 3 && <Step3 />}
        </div>
      </div>
    </WizardContext.Provider>
  );
}
